import { useEffect, useState } from 'react';
import { Button, Checkbox, brandName, productFullName, useToast } from '@dugzi/ui';
import { api } from '../lib/api';
import { useData } from '../lib/data';

interface Offered { email: string; name: string | null; times: number }

const QUIET = 'dz-contacts-write-to-quiet';

/** The one-time "People you write to" offer (Contacts PRD "Now"): the people written to most in Mail, none saved
 *  until ticked. Saving or "Not now" ends it for good; with nobody to offer it never shows. */
export function WriteToOffer() {
  const { reload } = useData();
  const toast = useToast();
  const [people, setPeople] = useState<Offered[]>([]);
  const [keep, setKeep] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  // Asked at most once per browser session when there is nobody to offer (the server also waits a week to ask Mail again).
  useEffect(() => {
    let live = true;
    let quiet = false;
    try { quiet = sessionStorage.getItem(QUIET) === '1'; } catch { /* storage blocked: just ask */ }
    if (quiet) return () => { live = false; };
    api<{ people: Offered[] }>('/api/offer/write-to').then((r) => {
      if (!live) return;
      setPeople(r.people);
      if (!r.people.length) { try { sessionStorage.setItem(QUIET, '1'); } catch { /* fine */ } }
    }).catch(() => {});
    return () => { live = false; };
  }, []);

  if (!people.length) return null;

  const answer = async (save: Offered[]) => {
    setBusy(true);
    try {
      const r = await api<{ saved: number }>('/api/offer/write-to', { body: { save: save.map(({ email, name }) => ({ email, name })) } });
      setPeople([]);
      if (r.saved) { await reload(); toast.show(`${r.saved} ${r.saved === 1 ? 'person' : 'people'} saved.`); }
    } catch (e) { toast.show((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <section className="write-to-offer" aria-labelledby="write-to-title" data-testid="write-to-offer">
      <h2 id="write-to-title">People you write to</h2>
      <p className="dz-muted dz-small">The people you write to most in {productFullName('mail')}. Select the ones to save; nothing is saved unless you say so, and {brandName()} won't ask again.</p>
      <ul className="import-list" aria-label="People you write to">
        {people.map((p) => (
          <li key={p.email}>
            <Checkbox checked={keep.has(p.email)} onChange={(e) => setKeep((k) => { const n = new Set(k); if (e.target.checked) n.add(p.email); else n.delete(p.email); return n; })}
              label={<span className="import-row"><strong>{p.name || p.email}</strong><span className="dz-muted dz-small">{p.name ? p.email : ''}</span></span>} />
          </li>
        ))}
      </ul>
      <div className="row-actions">
        <Button variant="primary" loading={busy} disabled={!keep.size} onClick={() => answer(people.filter((p) => keep.has(p.email)))} data-testid="write-to-save">Save {keep.size} {keep.size === 1 ? 'person' : 'people'}</Button>
        <Button variant="ghost" disabled={busy} onClick={() => answer([])} data-testid="write-to-dismiss">Not now</Button>
      </div>
    </section>
  );
}
